// The web's own words in English or Vietnamese (D31, R10-Q16). English is the source: every
// area's words live in `en/<area>.ts`; `vi/<area>.ts` must have every key (a missing one fails
// the build). The server writes notification titles, mails and refusals in the person's language
// itself (R10-B11, `backend/app/i18n.py`; the web says which on each request); Vantage's own lines
// in conversations (card text, system lines) stay as written.
//
// `t()` reads the language the shell set; the signed-in shell mounts again when it changes
// (routes/_app.tsx keys it on the language), so every word follows without each component
// subscribing.

import { readLocal, writeLocal } from "../lib/storage";
import { type Dict, en } from "./en";
import { vi } from "./vi";

export type Lang = "en" | "vi";
type Area = keyof Dict;
export type Key = { [A in Area]: `${A}.${Extract<keyof Dict[A], string>}` }[Area];
/** The keys with a `_one` / `_other` pair, by their stem ("chat.replies"). */
export type CountKey = {
  [A in Area]: `${A}.${Extract<keyof Dict[A], `${string}_one`> extends `${infer S}_one` ? S : never}`;
}[Area];

const DICTS: Record<Lang, Dict> = { en, vi };

// R10-B12: before `/me` is known (the sign-in page, a first paint) the words are in the
// language last used in this browser, else the browser's own: a Vietnamese browser signs in
// in Vietnamese.
const REMEMBERED = "vx.lang";

// What this browser keeps: written only when it changes (the shell sets the language on every
// render, review R10.8).
let kept = readLocal(REMEMBERED);

function firstGuess(): Lang {
  if (kept === "vi" || kept === "en") return kept;
  const said = typeof navigator === "undefined" ? "" : (navigator.language ?? "");
  return /^vi\b/i.test(said) ? "vi" : "en";
}

let current: Lang = firstGuess();
if (typeof document !== "undefined") document.documentElement.lang = current;

/** The language the words are in now. */
export function lang(): Lang {
  return current;
}

/** Set by the shell from `/me`'s `ui_language`, and kept for this browser's next sign-in. */
export function setLang(next: Lang | null | undefined): void {
  current = next === "vi" ? "vi" : "en";
  if (kept !== current) {
    writeLocal(REMEMBERED, current);
    kept = current;
  }
  if (typeof document !== "undefined") document.documentElement.lang = current;
}

/** The locale dates and numbers follow (R10-Q16: dates in vi-VN). */
export function dateLocale(): string {
  return current === "vi" ? "vi-VN" : "en-GB";
}

function fill(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (all, name: string) =>
    name in vars ? String(vars[name]) : all,
  );
}

function lookup(key: string): string {
  const dot = key.indexOf(".");
  const area = key.slice(0, dot) as Area;
  const name = key.slice(dot + 1);
  const pick = (d: Dict) => (d[area] as Record<string, string> | undefined)?.[name];
  return pick(DICTS[current]) ?? pick(en) ?? key;
}

/** A word or sentence: `t("chat.send")`, `t("chat.mentioned", { name: "Linh" })`. */
export function t(key: Key, vars?: Record<string, string | number>): string {
  return fill(lookup(key), vars);
}

/** A count's words: `tn("chat.replies", 3)` reads `chat.replies_one` or `chat.replies_other`,
 *  with `{n}` filled in. */
export function tn(key: CountKey, n: number, vars?: Record<string, string | number>): string {
  return fill(lookup(`${key}_${n === 1 ? "one" : "other"}`), { n, ...vars });
}
